import { useCallback, useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { mutate } from 'swr'
import shallow from 'zustand/shallow'

import { ClientSessionState } from '@frame/lib/api/client-session/types'
import { InternalError } from '@frame/lib/errors'
import { useStore } from '@frame/lib/stores/useStore'
import { routes, sessionStateRoutes } from '@frame/routes'
import { ErrorCode } from '@script/types'

import { useRootContext } from '../context'

function getNextRoute({ sessionState, error }: { sessionState: ClientSessionState; error?: InternalError }) {
  // Consolidated logic to determine the next route based on the session state and error. This is used in both the hook
  // and the component.
  if (error) {
    return error.code === ErrorCode.Unauthorized ? routes.unauthorized : routes.error
  }

  return sessionStateRoutes[sessionState]
}

export function useAutoNavigate() {
  // Hook to automatically navigate to the next route based on the session state. This is meant to be used in components
  // that want to auto-navigate at a specific time.
  const navigate = useNavigate()
  const location = useLocation()
  const sessionStateRef = useRef<ClientSessionState | null>(null)
  const { sessionState, error } = useStore(
    ({ session, error }) => ({
      sessionState: session?.state ?? ClientSessionState.INITIALIZED,
      error,
    }),
    shallow
  )
  const { actions } = useRootContext()

  useEffect(() => {
    if (sessionStateRef.current !== sessionState) {
      sessionStateRef.current = sessionState
    }
  }, [sessionState])

  const autoNavigate = useCallback(() => {
    navigate(getNextRoute({ sessionState: sessionStateRef.current || sessionState, error }), {
      replace: true,
      state: location.state,
    })
  }, [sessionStateRef, sessionState, error, location.state, navigate])

  const mutateAndNavigate = useCallback(() => {
    mutate(['all', 'tasks'])
    autoNavigate()
  }, [autoNavigate])

  const redirectToSessionStateRoute = useCallback(async () => {
    const data = await actions.getSession()

    if (data?.state && location.pathname !== sessionStateRoutes[data.state]) {
      navigate(sessionStateRoutes[data.state])
    }
  }, [location.pathname, actions, navigate])

  return {
    autoNavigate,
    mutateAndNavigate,
    redirectToSessionStateRoute,
  }
}
